import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import AppInfoFooter from "../../../components/AppInfoFooter";
import Button from "../../../components/Button";
import { EmailInput } from "../../../components/InputFields";
import Link from "../../../components/Link";
import MaterialIcon from "../../../components/MaterialIcon";
import Page from "../../../components/Page";
import { useSendForgotPasswordEmailMutation } from "../../../data/queries";
import { emailRegex } from "../../../model/Constants";
import cn from "../../../util/cn";

type ForgotPasswordPageStateParams = {
  email?: string;
};

type ForgotPasswordFormData = {
  email: string;
};

const ForgotPasswordPage = () => {
  const locationState = useLocation().state as ForgotPasswordPageStateParams;
  const { register, handleSubmit, formState, getValues } = useForm<ForgotPasswordFormData>({
    mode: "onBlur",
    defaultValues: { email: locationState?.email ?? "" },
  });

  const {
    mutateAsync: sendForgotPasswordEmail,
    isSuccess: isForgotPasswordMailSent,
    isPending: isSendingForgotPasswordEmail,
    error: sendForgotPasswordEmailError,
  } = useSendForgotPasswordEmailMutation();

  const { errors } = formState;

  const validateEmail = (email: string) => emailRegex.test(email) || "Please enter a valid email";

  const onSubmit = async (formData: ForgotPasswordFormData) => {
    const { email } = formData;
    await sendForgotPasswordEmail({ email: email });
  };

  return (
    <Page className="bg-gray-50">
      <div className="flex h-full flex-col justify-between">
        <div
          className={cn("mx-auto mt-10 w-full max-w-[480px] rounded-xl bg-white p-12 shadow-2xl")}
        >
          {isForgotPasswordMailSent ? (
            <div className="flex flex-col items-center">
              <h2 className="mb-8 text-center text-4xl text-primary/90">
                Reset password mail sent successfully
              </h2>
              <p
                className="material-icons-outlined mb-8 text-green-600"
                style={{ fontSize: "100px" }}
              >
                check_circle
              </p>
              <p className="mb-8 text-pretty text-center text-primary/90">
                A reset password email was sent to {getValues("email")}.<br />
                Please check your email.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
              <h2 className="mb-4 text-center text-4xl text-primary/90">Forgot password</h2>
              <h3 className="mb-8 text-pretty text-center text-lg text-primary/90">
                We will send a password reset link to your email address
              </h3>
              {sendForgotPasswordEmailError && (
                <div className="my-4 flex w-full items-center rounded-md border border-red-200 bg-red-100 p-4">
                  <span className="material-icons me-4 text-red-600">error</span>
                  <p className="text-red-600">
                    Something went wrong. Please try again later, or contact Bloomlife.
                  </p>
                </div>
              )}
              <label htmlFor="email" className="block leading-6 text-primary/90">
                Email address
              </label>
              <EmailInput
                id="email"
                autoComplete="username"
                className="mt-2"
                disabled={isSendingForgotPasswordEmail}
                {...register("email", {
                  required: "Please enter your email address",
                  validate: validateEmail,
                })}
              />
              <p className={cn("mt-2 text-sm italic text-red-600", errors.email ? "" : "hidden")}>
                {errors.email?.message}
              </p>
              <Button
                variant="primary"
                type="submit"
                disabled={isSendingForgotPasswordEmail}
                className="mt-8 w-full text-base"
              >
                Reset password
              </Button>
              <Link
                to="/login"
                state={{ email: getValues("email") }}
                replace={true}
                className="mt-4 flex items-center self-center hover:no-underline"
              >
                <MaterialIcon iconName="chevron_left" />
                Back to sign in
              </Link>
            </form>
          )}
        </div>
        <AppInfoFooter className="mt-auto p-4" />
      </div>
    </Page>
  );
};

export default ForgotPasswordPage;
